import { useEffect, useRef, useState } from "react";
import { products } from "@/mocks/products";
import ProductCard from "./ProductCard";
import CategoryBanner from "./CategoryBanner";

interface Props {
  activeCategory: string;
}

const sortOptions = [
  { value: "default", label: "預設排序" },
  { value: "price-asc", label: "價格由低到高" },
  { value: "price-desc", label: "價格由高到低" },
];

export default function ProductsGrid({ activeCategory }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Reset visibility on category change for re-animation
  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, [activeCategory]);

  const filtered = activeCategory === "all"
    ? products
    : products.filter((p) => p.category === activeCategory);

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "price-asc") return a.price - b.price;
    if (sortBy === "price-desc") return b.price - a.price;
    return 0;
  });

  const currentSort = sortOptions.find((s) => s.value === sortBy)!;

  return (
    <section className="py-14 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Category Banner */}
        {activeCategory !== "all" && (
          <CategoryBanner categoryId={activeCategory} />
        )}

        {/* Toolbar */}
        <div
          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <p
            className="text-[#7a6a5a] text-[1rem]"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            共 <strong className="text-[#2d2520]">{sorted.length}</strong> 件商品
          </p>

          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-3 px-5 py-2.5 border border-[#e8e0d5] rounded-full text-[1rem] text-[#5a4e45] cursor-pointer hover:border-[#8b6f47] transition-colors whitespace-nowrap bg-white"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              <i className="ri-sort-desc text-[#8b6f47]"></i>
              {currentSort.label}
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${sortOpen ? "rotate-180" : ""}`}></i>
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#e8e0d5] rounded-xl overflow-hidden z-20">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    className={`w-full text-left px-5 py-3 text-[1rem] cursor-pointer transition-colors ${
                      sortBy === opt.value
                        ? "bg-[#f5ede0] text-[#8b6f47] font-medium"
                        : "text-[#5a4e45] hover:bg-[#faf8f4]"
                    }`}
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {opt.value === sortBy && <i className="ri-check-line mr-2 text-[#8b6f47]"></i>}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
          data-product-shop
        >
          {sorted.map((product, i) => (
            <div
              key={product.id}
              className="transition-all duration-700"
              style={{
                transitionDelay: `${Math.min(i * 60, 400)}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
              }}
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>

        {/* Empty State */}
        {sorted.length === 0 && (
          <div className="text-center py-24">
            <i className="ri-inbox-line text-5xl text-[#c9a96e] mb-4 block"></i>
            <p
              className="text-[#7a6a5a] text-[1.1875rem]"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              此分類暫無商品
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
